"use client";

import {
  createContext,
  createElement,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { buildSearchEngine, type SearchEngine } from "@coveo/headless";
import { useLocale } from "next-intl";
import { useConsent } from "lib/consent/useConsent";

const SearchEngineContext = createContext<SearchEngine | null>(null);

const QUERY_OPERATORS = new Set(["AND", "OR", "NOT", "NEAR"]);
const MIN_WILDCARD_TERM_LENGTH = 2;

// Append a trailing `*` to each bare keyword in a Coveo basic expression so
// that typing `le` matches `lemon`. Skips quoted phrases, field expressions,
// negations, operators, and terms that already use wildcards — these would
// either break parsing or duplicate intent.
const isWhitespace = (char: string): boolean => /\s/.test(char);

const findTokenEnd = (value: string, start: number): number => {
  let end = start;

  while (end < value.length && !isWhitespace(value[end])) {
    end++;
  }

  return end;
};

const getQuotedPhrase = (
  value: string,
  start: number,
): { token: string; nextIndex: number } => {
  const end = value.indexOf('"', start + 1);
  const stop = end === -1 ? value.length : end + 1;

  return {
    token: value.substring(start, stop),
    nextIndex: stop,
  };
};

const shouldAppendWildcard = (token: string): boolean => {
  const isOperator = QUERY_OPERATORS.has(token.toUpperCase());
  const isQualified = /^[@$\-+(]/.test(token);
  const hasWildcard = token.includes("*") || token.includes("?");
  const isTooShort = token.length < MIN_WILDCARD_TERM_LENGTH;

  return !(isOperator || isQualified || hasWildcard || isTooShort);
};

const appendWildcardsToKeywords = (q: string): string => {
  const result: string[] = [];
  let i = 0;

  while (i < q.length) {
    while (i < q.length && isWhitespace(q[i])) {
      i++;
    }

    if (i >= q.length) {
      break;
    }

    if (q[i] === '"') {
      const { token, nextIndex } = getQuotedPhrase(q, i);

      result.push(token);
      i = nextIndex;
      continue;
    }

    const tokenEnd = findTokenEnd(q, i);
    const token = q.substring(i, tokenEnd);

    result.push(shouldAppendWildcard(token) ? `${token}*` : token);

    i = tokenEnd;
  }

  return result.join(" ");
};

// Derive PreprocessRequest from `buildSearchEngine`'s public config type so we
// don't have to import from `@coveo/headless/dist/definitions/...` — that
// deep path makes Turbopack walk `dist/definitions/` and try to load the
// comment-prefixed `tsdoc-metadata.json` as a module.
type EngineConfig = Parameters<typeof buildSearchEngine>[0]["configuration"];
type PreprocessRequest = NonNullable<EngineConfig["preprocessRequest"]>;

const sdsWildcardPreprocessor: PreprocessRequest = (
  request,
  clientOrigin,
  metadata,
) => {
  try {
    if (clientOrigin !== "searchApiFetch" || metadata?.method !== "search")
      return request;
    if (typeof request.body !== "string") return request;

    const body = JSON.parse(request.body);
    if (typeof body.q !== "string" || body.q.trim() === "") return request;

    body.q = appendWildcardsToKeywords(body.q);
    body.wildcards = true;
    request.body = JSON.stringify(body);
  } catch {
    // fail-open: any parse/shape error should not break search
  }
  return request;
};

const buildScopedSearchEngine = (
  scope: string,
  locale: string,
  analyticsAllowed: boolean,
  instanceKey?: string,
): SearchEngine => {
  const engineScope = scope || "Global";

  const sdsScopeName = process.env.NEXT_PUBLIC_COVEO_SDS_SCOPE_NAME || "SDS";
  const isSdsIndex = engineScope.toLowerCase() === sdsScopeName.toLowerCase();

  const accessToken = isSdsIndex
    ? (process.env.NEXT_PUBLIC_COVEO_SDS_ACCESS_TOKEN ?? "")
    : (process.env.NEXT_PUBLIC_COVEO_ACCESS_TOKEN ?? "");
  const organizationId = isSdsIndex
    ? (process.env.NEXT_PUBLIC_COVEO_SDS_ORGANIZATION_ID ?? "")
    : (process.env.NEXT_PUBLIC_COVEO_ORGANIZATION_ID ?? "");
  const pipeline = isSdsIndex
    ? (process.env.NEXT_PUBLIC_COVEO_SDS_PIPELINE ?? "")
    : (process.env.NEXT_PUBLIC_COVEO_PIPELINE ?? "");
  const searchHub = isSdsIndex
    ? (process.env.NEXT_PUBLIC_COVEO_SDS_SEARCH_HUB ?? "")
    : (process.env.NEXT_PUBLIC_COVEO_SEARCH_HUB ?? "");

  return buildSearchEngine({
    configuration: {
      name: `${engineScope}_${locale}_${instanceKey || "default"}`,
      accessToken,
      organizationId,
      preprocessRequest: isSdsIndex ? sdsWildcardPreprocessor : undefined,
      analytics: { enabled: analyticsAllowed },
      search: {
        pipeline,
        searchHub,
        locale,
      },
    },
  });
};

export const SearchEngineProvider = ({
  engine,
  children,
}: {
  engine: SearchEngine;
  children: ReactNode;
}) => createElement(SearchEngineContext.Provider, { value: engine }, children);

export const usePageSearchEngine = (
  scope?: string,
  instanceKey?: string,
): SearchEngine => {
  const locale = useLocale();
  const analyticsAllowed = useConsent("performance");
  return useMemo(
    () =>
      buildScopedSearchEngine(
        scope || "Global",
        locale,
        analyticsAllowed,
        instanceKey,
      ),
    [scope, locale, analyticsAllowed, instanceKey],
  );
};

export const useSearchEngine = (
  scope?: string,
  instanceKey?: string,
): SearchEngine => {
  const engine = useContext(SearchEngineContext);
  const locale = useLocale();
  const analyticsAllowed = useConsent("performance");

  return useMemo(
    () =>
      engine ??
      buildScopedSearchEngine(
        scope || "Global",
        locale,
        analyticsAllowed,
        instanceKey,
      ),
    [engine, scope, locale, analyticsAllowed, instanceKey],
  );
};
